<script setup lang="ts">
import { useSideModals } from "~/composables/utils/useSideModals";
import { useCreateProjectStore } from "~/store/project/create-project.store";

const props = withDefaults(
  defineProps<{
    maxWidth?: string;
    /**
     * Stacking level of the backdrop. Raise it when this panel opens **over** something that already
     * floats — the assistant sits at 600 and would otherwise cover it.
     * Reference points: SideModal 200, GlobalModal 500, CenterModal 501, Copilot 600.
     */
    zIndex?: number;
  }>(),
  {
    maxWidth: "528px",
    zIndex: 200,
  },
);

const createProjectCoreStore = useCreateProjectStore();
const emit = defineEmits(["hide"]);

// Counts this panel in while it is on screen, so the floating Copilote button stands down for it.
const { registerSideModal } = useSideModals();
registerSideModal();

// Track if mouse down started inside the modal
const isMouseDownInside = ref(false);

function hideModal() {
  emit("hide");
}

function keyEscPress(e: KeyboardEvent) {
  if (e.key === "Escape") {
    hideModal();
  }
}

function handleMouseDown(e: MouseEvent) {
  // Check if mousedown started inside the modal container
  const modalContainer = document.querySelector(".side_modal__container");
  if (modalContainer && modalContainer.contains(e.target as Node)) {
    isMouseDownInside.value = true;
  } else {
    isMouseDownInside.value = false;
  }
}

function handleMouseUp(e: MouseEvent) {
  // Only close if both mousedown and mouseup happened outside the modal
  if (!isMouseDownInside.value && e.target === e.currentTarget) {
    hideModal();
  }
  isMouseDownInside.value = false;
}

onMounted(() => {
  document.addEventListener("keyup", keyEscPress);
});

onUnmounted(() => {
  document.removeEventListener("keyup", keyEscPress);
});
</script>

<template>
  <div class="side_modal_main" :style="{ zIndex: props.zIndex }" @mousedown="handleMouseDown" @mouseup="handleMouseUp">
    <div
      class="side_modal__container"
      :class="createProjectCoreStore.showSessionDetailsComplete ? 'h-full overflow-auto' : null"
      :style="{ maxWidth: props.maxWidth }"
    >
      <div class="side_modal__header">
        <button class="absolute left-[16px]" @click="hideModal">
          <img src="/icons/generic/arrow_left.svg" alt="" />
        </button>
        <div class="side_modal__header_title">
          <slot name="title"> Title </slot>
        </div>
      </div>

      <div
        class="side_modal__body body_regular text-primary-color h-full"
        :class="createProjectCoreStore.showSessionDetailsComplete ? 'overflow-auto' : 'overflow-y-auto'"
      >
        <slot name="content"></slot>
      </div>

      <slot name="footer"></slot>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "assets/scss/texts";
@use "assets/scss/modal";

.side_modal__container {
  @apply flex flex-col w-full;
  background: var(--white-main);
  border-radius: 12px 0 0 12px;
}

@media (max-width: 768px) {
  .side_modal__container {
    @apply max-w-full rounded-none;
  }
}

.side_modal__header {
  @apply flex items-center justify-center relative;
  box-shadow: var(--shadow-soft);

  button {
    @apply rounded-2xl p-[8px];
    &:hover {
      background: var(--grey-light);
    }
  }
}

.side_modal__header_title {
  @extend .heading_3_regular;
  @apply py-[20px];
  color: var(--text-secondary-color);
}

.side_modal__body {
  @apply px-[24px] pt-[16px];
}

.side_modal_main {
  @apply background_modal__grey flex justify-end;
}
</style>
